import React, { useState, useEffect } from "react";
import {
  MessageSquare,
  Users,
  Search,
  BookOpen,
  Volume2,
  Menu,
  X,
  LogOut,
  User,
  ArrowRight,
  HelpCircle,
  Bell,
  LayoutGrid,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ThemeToggle from "../ui/ThemeToggle";

import CampusIdentity from "./CampusIdentity";
import CampusFeed from "./CampusFeed";
import CampusProfile from "./CampusProfile";
import QAContainer from "./qa/QAContainer";
import PostModalManager from "./PostModalManager";
import NotificationFeed from "./NotificationFeed";
import UtilityHome from "./utilities/UtilityHome";
import GPACalculatorApp from "./utilities/GPACalculatorApp";
import { supabase } from "../../lib/supabase";
import logo from "../../logo2.png";

const CampusLayout = ({ onExit }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentUser, setCurrentUser] = useState(null);
  const [currentAlias, setCurrentAlias] = useState(null);
  const [currentUserAvatar, setCurrentUserAvatar] = useState(null);
  const [campusCode, setCampusCode] = useState(null);
  const [feedMode, setFeedMode] = useState("unified");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrollPositions, setScrollPositions] = useState({});
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeModalPost, setActiveModalPost] = useState(null); // { postId, qaId }
  const [utilityView, setUtilityView] = useState("home"); // "home" or "gpa-calculator"

  // Get view and userId from URL params
  const activeView = searchParams.get("view") || "feed";
  const visitedUserId = searchParams.get("userId") || null;

  const profile = currentUser
    ? {
        campus_alias: currentAlias,
        id: currentUser.id,
        campus_code: campusCode,
      }
    : null;

  useEffect(() => {
    const fetchCurrentUserAvatar = async () => {
      if (!currentUser) {
        setCurrentUserAvatar(null);
        return;
      }
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("avatar_url")
          .eq("id", currentUser.id)
          .single();
        if (error) {
          console.error("Error fetching user avatar:", error);
          setCurrentUserAvatar(null);
        } else {
          setCurrentUserAvatar(data?.avatar_url || null);
        }
      } catch (err) {
        console.error("Error fetching avatar:", err);
        setCurrentUserAvatar(null);
      }
    };
    fetchCurrentUserAvatar();
  }, [currentUser?.id]);

  // Fetch unread notification count
  useEffect(() => {
    if (!currentUser) return;

    const fetchUnreadCount = async () => {
      const { count, error } = await supabase
        .from("notifications")
        .select("*", { count: "exact", head: true })
        .eq("recipient_id", currentUser.id)
        .eq("is_read", false);

      if (!error) {
        setUnreadCount(count || 0);
      }
    };

    fetchUnreadCount();

    // Subscribe to NEW notifications for unread badge
    const channel = supabase
      .channel(`unread_count:${currentUser.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `recipient_id=eq.${currentUser.id}`,
        },
        () => {
          setUnreadCount((prev) => prev + 1);
        }
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [currentUser?.id]);

  // Save scroll position before navigating
  const saveScrollPosition = () => {
    const scrollContainer = document.querySelector(".h-full.overflow-y-auto");
    if (scrollContainer) {
      setScrollPositions(prev => ({
        ...prev,
        [activeView]: scrollContainer.scrollTop
      }));
    }
  };

  // Restore scroll position when returning to a view
  useEffect(() => {
    const scrollContainer = document.querySelector(".h-full.overflow-y-auto");
    if (scrollContainer && scrollPositions[activeView]) {
      setTimeout(() => {
        scrollContainer.scrollTop = scrollPositions[activeView];
      }, 0);
    }
  }, [activeView, scrollPositions]);

  const handleViewProfile = (userId) => {
    saveScrollPosition();
    setSearchParams({ view: "profile", userId });
    setMobileMenuOpen(false);
  };

  const handleMyProfile = () => {
    if (currentUser) {
      saveScrollPosition();
      setSearchParams({ view: "profile", userId: currentUser.id });
      setMobileMenuOpen(false);
    }
  };

  const handleFeedClick = () => {
    if (activeView === "feed") {
      // Refresh trigger removed - just navigate
      saveScrollPosition();
    } else {
      saveScrollPosition();
      setSearchParams({ view: "feed" });
    }
    setMobileMenuOpen(false);
  };

  const handleExitCampus = () => {
    localStorage.setItem('cuconnect_manual_exit', 'true');
    onExit && onExit();
  };

  const handleQAClick = () => {
    saveScrollPosition();
    setSearchParams({ view: "qa" });
    setMobileMenuOpen(false);
  };

  const handleNotificationsClick = () => {
    saveScrollPosition();
    setSearchParams({ view: "notifications" });
    setMobileMenuOpen(false);
  };

  const handleUtilitiesClick = () => {
    saveScrollPosition();
    setSearchParams({ view: "utilities" });
    setUtilityView("home");
    setMobileMenuOpen(false);
  };

  const handleNotificationClick = (notif) => {
    // Let PostModalManager handle the logic of which component to render
    setActiveModalPost({ postId: notif.post_id });
  };

  const navItems = [
    { id: "feed", label: "Feed", icon: Volume2, handler: handleFeedClick },
    { id: "qa", label: "Q&A", icon: HelpCircle, handler: handleQAClick },
    { id: "utilities", label: "Utilities", icon: LayoutGrid, handler: handleUtilitiesClick },
    { id: "notifications", label: "Notifications", icon: Bell, handler: handleNotificationsClick, badge: unreadCount },
    { id: "profile", label: "Profile", icon: User, handler: handleMyProfile },
  ];

  const renderView = () => {
    if (!currentUser) return null;

    switch (activeView) {
      case "feed":
        return (
          <CampusFeed
            communityId="feed"
            userAlias={currentAlias}
            userId={currentUser.id}
            userCampus={campusCode}
            userAvatar={currentUserAvatar}
            feedMode={feedMode}
            onSetFeedMode={setFeedMode}
            onViewProfile={handleViewProfile}
          />
        );
      case "qa":
        return (
          <QAContainer
            userAlias={currentAlias}
            userId={currentUser.id}
            userCampus={campusCode}
            onViewProfile={handleViewProfile}
          />
        );
      case "profile":
        return (
          <CampusProfile
            currentUserId={currentUser.id}
            visitedUserId={visitedUserId}
          />
        );
      case "notifications":
        return (
          <NotificationFeed
            userId={currentUser.id}
            onNotificationClick={handleNotificationClick}
            setUnreadCount={setUnreadCount}
          />
        );
      case "utilities":
        if (utilityView === "home") {
          return <UtilityHome onSelectUtility={(id) => setUtilityView(id)} />;
        }
        if (utilityView === "gpa-calculator") {
          return <GPACalculatorApp onBack={() => setUtilityView("home")} />;
        }
        return null;
      default:
        return null;
    }
  };

  if (!currentUser) {
    return (
      <div className="flex h-screen bg-gray-50 flex-col">
        <div className="p-4"></div>
        <CampusIdentity
          onVerified={(user, alias, code) => {
            setCurrentUser(user);
            setCurrentAlias(alias);
            setCampusCode(code);
            localStorage.removeItem('cuconnect_manual_exit');
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-slate-50 dark:bg-gradient-to-b dark:from-[#0a0e27] dark:via-[#0f1535] dark:to-[#0a0e27] font-sans overflow-hidden transition-colors duration-300">
      {/* Top Navigation Bar */}
      <header className="bg-white dark:bg-gradient-to-b dark:from-[#0f1535] dark:to-[#0a0e27]/80 border-b border-slate-200/50 dark:border-indigo-500/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between h-14 sm:h-16 px-3 sm:px-4 lg:px-6">
          {/* Logo & Brand */}
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <img
              src={logo}
              alt="Logo"
              className="w-8 h-8 sm:w-9 sm:h-9 object-contain drop-shadow-sm"
            />
            <h1 className="text-base sm:text-lg font-black text-gray-900 dark:text-white tracking-tight hidden sm:block">
              CUConnect
            </h1>
          </div>

          {/* Desktop Navigation - Center */}
          <nav className="hidden md:flex items-center gap-0.5 flex-1 justify-center">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={item.handler}
                className={`flex items-center gap-2 px-4 sm:px-5 py-2 rounded-xl font-bold text-xs sm:text-sm transition-all duration-300 relative ${
                  activeView === item.id
                    ? "bg-slate-900 text-white shadow-lg shadow-slate-900/10 scale-[1.02]"
                    : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10"
                }`}
              >
                <item.icon size={18} />
                <span>{item.label}</span>
                {item.id === 'notifications' && unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center bg-indigo-600 text-white text-[10px] font-black rounded-full border-2 border-white dark:border-[#0f1535] shadow-lg animate-in zoom-in duration-300">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <ThemeToggle />
            
            <div className="w-px h-6 bg-slate-200 dark:bg-indigo-500/20 mx-1 hidden sm:block"></div>

            {/* User Menu Button - Hidden on Mobile (Redundant with bottom nav) */}
            <button
              onClick={handleMyProfile}
              className="hidden md:flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 transition-colors group"
            >
              {currentUserAvatar ? (
                <img
                  src={currentUserAvatar}
                  alt={profile?.campus_alias}
                  className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg object-cover border border-indigo-200 dark:border-indigo-400"
                />
              ) : (
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 dark:from-indigo-600 dark:to-purple-600 flex items-center justify-center text-white font-bold text-[10px] sm:text-xs border border-indigo-200 dark:border-indigo-400">
                  {profile?.campus_alias?.[0]}
                </div>
              )}
              <span className="text-[10px] sm:text-xs font-bold text-slate-900 dark:text-white hidden sm:block">
                {profile?.campus_alias}
              </span>
            </button>

            {/* Mobile Menu Button - More spacious tap target */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden p-2 sm:p-2.5 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10 rounded-xl transition-colors ml-1"
            >
              <Menu size={20} className="sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content Area - Fills remaining space */}
      <main className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto custom-scrollbar">
          <div className="w-full px-3 sm:px-4 py-3 sm:py-4 md:px-6 lg:px-8 max-w-6xl mx-auto">
            {renderView()}
          </div>
        </div>
      </main>

      {/* Global Post Modal Manager */}
      <PostModalManager 
        activePostId={activeModalPost?.postId}
        onClose={() => setActiveModalPost(null)}
        currentUser={{ id: currentUser.id, alias: currentAlias, campus_code: campusCode }}
        onViewProfile={handleViewProfile}
      />

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden bg-white/90 dark:bg-[#0f1535]/90 backdrop-blur-xl border-t border-slate-100 dark:border-indigo-500/10 px-3 py-1.5 flex justify-around items-center sticky bottom-0">
        {navItems.map((item) => (
          <button
            key={item.id}
            onClick={item.handler}
            className={`flex flex-col items-center gap-0.5 py-1.5 px-2 transition-all relative ${
              activeView === item.id
                ? "text-slate-900 dark:text-indigo-300 scale-105"
                : "text-slate-400"
            }`}
          >
            <item.icon size={20} />
            <span className="text-[10px] font-bold uppercase tracking-wider">
              {item.label}
            </span>
            {item.id === 'notifications' && unreadCount > 0 && (
              <span className="absolute top-1 right-3 flex h-4 w-4 items-center justify-center bg-indigo-600 text-white text-[9px] font-black rounded-full border border-white dark:border-[#0f1535] shadow-sm animate-in zoom-in duration-300">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
        ))}
      </nav>

      {/* Mobile Menu Drawer */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="absolute inset-y-0 right-0 w-64 bg-white dark:bg-gradient-to-b dark:from-[#0f1535] dark:to-[#0a0e27] shadow-xl flex flex-col animate-in slide-in-from-right duration-300">
            <div className="p-4 border-b border-slate-100 dark:border-indigo-500/10 flex justify-between items-center">
              <span className="font-black text-slate-900 dark:text-white">
                Menu
              </span>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 hover:bg-slate-100 dark:hover:bg-white/10 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 p-4 space-y-2">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={item.handler}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg font-bold transition-all ${
                    activeView === item.id
                      ? "bg-slate-900 text-white shadow-lg"
                      : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <item.icon size={20} />
                    <span>{item.label}</span>
                  </div>
                  {item.id === 'notifications' && unreadCount > 0 && (
                    <span className="px-2 py-0.5 bg-indigo-600 text-white text-[10px] font-black rounded-full">
                      {unreadCount}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <div className="p-4 border-t border-slate-100 dark:border-indigo-500/10">
              <button
                onClick={() => {
                  handleExitCampus();
                  setMobileMenuOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-red-600 dark:text-red-400 font-bold hover:bg-red-50 dark:hover:bg-red-950/20 rounded-lg transition-colors"
              >
                <LogOut size={20} className="rotate-180" />
                <span>Exit Campus</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampusLayout;
